import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Upload, Check, Info, Camera, Layers, Droplet, Frame, X, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import Seo from "../components/Seo";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { ShippingNotice } from "../components/ShippingNotice";
import { RichContent } from "../components/RichText";
import { api, money } from "../lib/api";
import { useCart } from "../lib/cart";
import { useContent } from "../context/ContentContext";

const STEP_ICONS = [Camera, Layers, Droplet, Frame];

export default function CustomEtching() {
  const navigate = useNavigate();
  const { addItem } = useCart();
  const { p } = useContent();
  const c = p("custom_etching");
  const fileRef = useRef();
  const [settings, setSettings] = useState(null);
  const [upload, setUpload] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [sizeLabel, setSizeLabel] = useState("");
  const [additional, setAdditional] = useState(0);
  const [instructions, setInstructions] = useState("");

  useEffect(() => {
    api.get("/settings").then((r) => {
      setSettings(r.data);
      if (r.data.custom_sizes?.length) setSizeLabel(r.data.custom_sizes[0].label);
    });
  }, []);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) { toast.error("Please upload an image under 25MB."); return; }
    setUploading(true);
    setPreviewUrl(URL.createObjectURL(file));
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUpload({ id: r.data.id, filename: r.data.filename });
      toast.success("Photograph uploaded");
    } catch {
      toast.error("Upload failed. Please try again.");
      setPreviewUrl(null);
    } finally {
      setUploading(false);
    }
  };

  const sizeDelta = settings ? ((settings.custom_sizes || []).find((s) => s.label === sizeLabel)?.price_delta || 0) : 0;
  const total = settings ? (settings.custom_base_price + sizeDelta + additional * settings.additional_print_price) : 0;
  const additionalDesc = settings ? ((c.options.additional_desc || "").replace("{price}", money(settings.additional_print_price, settings.currency))) : "";


  const addToCart = () => {
    if (!upload) { toast.error("Please upload a photograph first."); return; }
    addItem({
      kind: "custom", product_slug: "custom-etching", title: "Custom Photogravure Etching",
      price: total, image: previewUrl, quantity: 1,
      size_label: sizeLabel || null, additional_prints: additional, instructions,
      upload_id: upload.id, upload_filename: upload.filename,
    });
    toast.success("Custom etching added to cart");
    navigate("/cart");
  };

  return (
    <div data-testid="custom-etching-page">
      <Seo title="Custom Etching — CaminoSapiens" description={settings ? `Commission a hand-pulled photogravure etching from your own photograph. From ${money(settings.custom_base_price, settings.currency)}, including plate creation and first print.` : "Commission a hand-pulled photogravure etching from your own photograph, including plate creation and first print."} image="https://images.pexels.com/photos/6620992/pexels-photo-6620992.jpeg?auto=compress&cs=tinysrgb&w=1200" />

      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14">
        <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Custom Etching" }]} />
        <div className="max-w-3xl">
          <p className="overline mb-4">{c.header.eyebrow}</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-tight">{c.header.heading}</h1>
          <p className="text-[#4A4947] mt-6 leading-relaxed text-lg">
            {c.header.body}
          </p>
          <a href="/#testimonials" data-testid="custom-proof-link" className="mt-6 inline-flex items-center gap-1.5 text-sm link-underline text-[#4A4947] hover:text-[#B35D4B] transition-colors">See where our etchings have found a home <ArrowRight className="w-3.5 h-3.5" /></a>
        </div>
      </div>

      {/* Steps */}
      <div className="bg-[#F0EFEA] border-y border-[#E4E1DB]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {(c.steps.items || []).map((s, i) => {
            const Icon = STEP_ICONS[i] || Camera;
            return (
              <motion.div key={i} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.08 }}>
                <div className="w-12 h-12 border border-[#1C1B1A] flex items-center justify-center mb-4"><Icon className="w-5 h-5" strokeWidth={1.4} /></div>
                <p className="font-display text-xl">{s.t}</p>
                <p className="text-sm text-[#4A4947] mt-1">{s.d}</p>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Configurator */}
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 grid lg:grid-cols-2 gap-16">
        <div>
          <p className="overline mb-4">{c.upload.eyebrow}</p>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="photo-upload-input" />
          {!previewUrl ? (
            <button onClick={() => fileRef.current?.click()} data-testid="photo-upload-button"
              className="w-full border border-dashed border-[#9a9793] py-20 flex flex-col items-center justify-center hover:border-[#B35D4B] transition-colors">
              <Upload className="w-8 h-8 text-[#4A4947]" strokeWidth={1.4} />
              <span className="text-sm text-[#4A4947] mt-4">{c.upload.button_text}</span>
              <span className="text-xs text-[#9a9793] mt-1">{c.upload.button_hint}</span>
            </button>
          ) : (
            <div className="relative border border-[#E4E1DB]">
              <img src={previewUrl} alt="Your uploaded photograph preview" className="w-full h-80 object-cover" />
              {uploading && <div className="absolute inset-0 bg-black/40 flex items-center justify-center text-white text-sm">Uploading…</div>}
              {upload && !uploading && (
                <div className="absolute top-3 left-3 bg-[#1C1B1A] text-white text-xs px-3 py-1 flex items-center gap-1"><Check className="w-3 h-3" /> Uploaded</div>
              )}
              <button onClick={() => { setPreviewUrl(null); setUpload(null); }} className="absolute top-3 right-3 bg-white/90 p-1.5" data-testid="photo-remove"><X className="w-4 h-4" /></button>
            </div>
          )}

          <div className="mt-6 bg-[#F0EFEA] border border-[#E4E1DB] p-5 text-sm text-[#4A4947]" data-testid="photo-guidance">
            <p className="flex items-start gap-2"><Info className="w-4 h-4 mt-0.5 shrink-0" /> <span>{c.upload.guidance}</span></p>
          </div>
          <p className="text-sm text-[#4A4947] mt-4 leading-relaxed">
            {c.upload.review_note}
          </p>
        </div>

        {/* Options + price */}
        <div className="lg:sticky lg:top-28 h-fit">
          {!settings ? (
            <div className="animate-pulse space-y-4" data-testid="custom-options-skeleton">
              <div className="h-8 w-1/3 bg-[#F0EFEA]" />
              <div className="h-24 bg-[#F0EFEA]" />
              <div className="h-40 bg-[#F0EFEA]" />
            </div>
          ) : (
          <>
          {settings.custom_sizes?.length > 0 && (
            <div className="mb-8">
              <p className="overline mb-4">2 · {c.options.size_eyebrow}</p>
              <div className="flex flex-wrap gap-3">
                {settings.custom_sizes.map((s) => (
                  <button key={s.label} onClick={() => setSizeLabel(s.label)} data-testid={`size-${s.label}`}
                    className={`px-4 py-3 text-sm border ${sizeLabel === s.label ? "border-[#1C1B1A] bg-[#1C1B1A] text-white" : "border-[#E4E1DB] hover:border-[#1C1B1A]"}`}>
                    {s.label}{s.price_delta ? ` · +${money(s.price_delta, settings.currency)}` : ""}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="mb-8">
            <p className="overline mb-4">{settings.custom_sizes?.length ? "3" : "2"} · {c.options.additional_eyebrow}</p>
            <p className="text-sm text-[#4A4947] mb-3">{additionalDesc}</p>
            <div className="flex items-center border border-[#E4E1DB] w-fit">
              <button onClick={() => setAdditional(Math.max(0, additional - 1))} className="px-4 py-2 text-lg" data-testid="additional-minus">−</button>
              <span className="px-6 py-2 border-x border-[#E4E1DB]" data-testid="additional-count">{additional}</span>
              <button onClick={() => setAdditional(additional + 1)} className="px-4 py-2 text-lg" data-testid="additional-plus">+</button>
            </div>
          </div>

          <div className="mb-8">
            <p className="overline mb-4">{c.options.notes_eyebrow}</p>
            <textarea value={instructions} onChange={(e) => setInstructions(e.target.value)} rows={3} data-testid="instructions-input"
              placeholder={c.options.notes_placeholder}
              className="w-full border border-[#E4E1DB] bg-transparent p-3 text-sm outline-none focus:border-[#1C1B1A]" />
          </div>

          <div className="border-t border-[#1C1B1A] pt-6">
            <ShippingNotice className="mb-6" />
            <div className="flex justify-between text-sm text-[#4A4947] py-1"><span>{c.options.line_item_label}</span><span>{money(settings.custom_base_price + sizeDelta, settings.currency)}</span></div>
            {additional > 0 && <div className="flex justify-between text-sm text-[#4A4947] py-1"><span>{additional} additional print{additional > 1 ? "s" : ""}</span><span>{money(additional * settings.additional_print_price, settings.currency)}</span></div>}
            <div className="flex justify-between items-baseline mt-4">
              <span className="font-display text-xl">{c.options.total_label}</span>
              <span className="font-display text-3xl" data-testid="custom-total">{money(total, settings.currency)}</span>
            </div>
            <button onClick={addToCart} disabled={uploading} data-testid="custom-add-to-cart"
              className="w-full mt-6 bg-[#1C1B1A] text-[#F9F8F6] py-4 text-sm hover:bg-[#B35D4B] transition-colors disabled:opacity-50">
              {c.options.add_to_cart_label}
            </button>
            <p className="text-xs text-[#9a9793] mt-3 text-center">{c.options.included_note}</p>
          </div>
          </>
          )}
        </div>
      </div>

      {/* Details */}
      <div className="bg-[#F0EFEA] border-t border-[#E4E1DB]">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 py-20 space-y-8 text-[#4A4947]">
          {(c.details.items || []).map((d, i) => (
            <div key={i}><p className="overline mb-2">{d.eyebrow}</p><RichContent html={d.body} /></div>
          ))}
        </div>
      </div>
    </div>
  );
}
